import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IMyOwnTractorsReqBody } from '../../../../api/user/mutations/patchMyOwnTractors';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { SlideConfirmModal } from '../../../../components/common/modal/slide/SlideConfirmModal';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { ownTractorPageInfo, pageInfo, sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ownTractorSurveyConstants } from '../../../../constants/survey/ownTractorSurvey';
import { usePatchMyOwnTractorsMutation } from '../../../../hooks/api/user/mutations/usePatchMyOwnTractors.mutation';
import { usePostMyOwnTractorsMutation } from '../../../../hooks/api/user/mutations/usePostMyOwnTractors.mutation';
import { useRedirectToStoredPage } from '../../../../hooks/page/useRedirectToStoredPage';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { InputValue } from '../../../../interfaces/Element.interfaces';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { PriceHelperText } from '../../components/PriceHelperText';
import { OwnTractorSurveyPageWrapper } from '../OwnTractorSurveyPageWrapper';
import { ampli } from '../../../../ampli';
import { userOwnTractorSurveySlice } from '../../../../store/userOwnTractorSurveySlice';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { createUserOwnTractorTempData } from '../../../../utils/creator/createUserOwnTractorTempData.util';
import { TEMP_DATA_KEYS } from '../../../../constants/tempData/tempData.contant';
import { useUserTempData } from '../../../../hooks/userTempData/useUserTempData';
import { Helmet } from 'react-helmet';

export const OwnTractorSurveyMarketPricePage = () => {
  const navigate = useNavigate();
  const Popup = usePopup();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const [marketPrice, setMarketPrice] = useState<number>();

  const [isContinueAtSellTractorModalOpen, setIsContinueAtSellTractorModalOpen] = useState<boolean>(false);
  const [isEnrollCompletePopupModalOpen, setIsEnrollCompletePopupModalOpen] = useState<boolean>(false);
  const [isRerequestPopupModalOpen, setIsRerequestPopupModalOpen] = useState<boolean>(false);
  const [isSyetemErrorPopupModalOpen, setIsSyetemErrorPopupModalOpen] = useState<boolean>(false);
  const [isNeedJoinModalOpen, setIsNeedJoinModalOpen] = useState<boolean>(false);

  const { getUserTempData, setUserTempData } = useUserTempData();

  const userOwnTractorSurveySelector = useAppSelector((state) => state.userOwnTractorSurvey);

  const dispatch = useAppDispatch();
  const userSellTractorActions = userSellTractorSurveySlice.actions;
  const userOwnTractorActions = userOwnTractorSurveySlice.actions;

  const ownTractorSurveySteps = ownTractorSurveyConstants.steps;
  const currentStep = ownTractorSurveySteps.STEP2;
  const steps = Object.values(ownTractorSurveySteps);
  const { openNotification } = useNotification();

  const { navigateToStoredPage } = useRedirectToStoredPage();

  const confirmSlideModal = () => {
    setIsContinueAtSellTractorModalOpen(false);
    dispatch(
      userSellTractorActions.changeSellTractorRequiredInputs({
        brand: userOwnTractorSurveySelector.brand,
        brandId: userOwnTractorSurveySelector.brandId,
        engineOutputInPs: userOwnTractorSurveySelector.engineOutputInPs,
        manufacturedYear: userOwnTractorSurveySelector.manufacturedYear,
        model: userOwnTractorSurveySelector.model,
        operationTime: userOwnTractorSurveySelector.operationTime,
      }),
    );
    dispatch(userSellTractorActions.changeHopeSellingPrice(marketPrice ?? 0));
    ampli.pvmCompleteOwnedThenSellRegister();
    ReactGA.event('pvm_complete_owned_then_sell_register');
    navigate(sellTractorPageInfo.overviewPage.path, { replace: true });
  };

  const closeSlideModal = () => {
    ampli.pvmCompleteOwnedThenSellCancel();
    ReactGA.event('pvm_complete_owned_then_sell_cancel');
    setIsContinueAtSellTractorModalOpen(false);
    setIsEnrollCompletePopupModalOpen(true);

    navigate(ownTractorPageInfo.completePage.path, {
      replace: true,
    });
  };

  const confirmPopupModal = () => {
    setIsEnrollCompletePopupModalOpen(false);
    navigateToStoredPage();
  };

  const { mutate: postUsersOwnTractor, isLoading: isPostUsersOwnTractorLoading } = usePostMyOwnTractorsMutation();
  const { mutate: patchUsersOwnTractor, isLoading: isPatchUsersOwnTractorLoading } = usePatchMyOwnTractorsMutation();

  const updateUserOwnTractor = () => {
    const payload: IMyOwnTractorsReqBody = {
      marketPrice: marketPrice ? marketPrice * 10000 : null,
      manufacturedYear: Number(userOwnTractorSurveySelector.manufacturedYear),
      operationTime: Number(userOwnTractorSurveySelector.operationTime),
    };

    if (!userOwnTractorSurveySelector.isApprovedTractor) {
      Object.assign(payload, {
        brandId: userOwnTractorSurveySelector.brandId,
        model: userOwnTractorSurveySelector.model,
        engineOutputInPs: Number(userOwnTractorSurveySelector.engineOutputInPs),
      });
    }

    patchUsersOwnTractor(
      {
        ownTractorId: userOwnTractorSurveySelector.ownTractorIdForEdit!,
        reqBody: payload,
      },
      {
        onSuccess: (data) => {
          if (isRerequestPopupModalOpen) {
            setIsRerequestPopupModalOpen(false);
          }

          openNotification({ message: '수정이 성공적으로 완료 되었어요.' });
          navigate(pageInfo.myInfoOwnTractorListPage.path, { replace: true });
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            if (!error.response) {
              setIsRerequestPopupModalOpen(true);
            } else {
              setIsSyetemErrorPopupModalOpen(true);
            }
          }
        },
      },
    );
  };

  const handleSurveySubmit = () => {
    if (userOwnTractorSurveySelector.ownTractorIdForEdit) {
      updateUserOwnTractor();
      return;
    }
    if (!isUserLoggedIn) {
      const { newOwnTractorTempData } = createUserOwnTractorTempData({
        formData: { ...userOwnTractorSurveySelector, marketPrice },
      });

      const { userTempData } = getUserTempData();
      userTempData.set(TEMP_DATA_KEYS.OWN_TRACTOR_SURVEY, newOwnTractorTempData);
      setUserTempData({ newTempData: userTempData });

      setIsNeedJoinModalOpen(true);
      return;
    }

    postUsersOwnTractor(
      {
        reqBody: {
          brandId: userOwnTractorSurveySelector.brandId!,
          model: userOwnTractorSurveySelector.model!,
          engineOutputInPs: Number(userOwnTractorSurveySelector.engineOutputInPs),
          manufacturedYear: Number(userOwnTractorSurveySelector.manufacturedYear),
          operationTime: Number(userOwnTractorSurveySelector.operationTime),
          marketPrice: marketPrice ? marketPrice * 10000 : null,
        },
      },
      {
        onSuccess: (data) => {
          if (isRerequestPopupModalOpen) {
            setIsRerequestPopupModalOpen(false);
          }

          ampli.pvmOwnTractorComplete();
          ReactGA.event('pvm_own_tractor_complete', { category: 'pvm', action: 'pvm_own_tractor_complete' });

          if (userOwnTractorSurveySelector.isFromJoinCompletePage) {
            dispatch(userOwnTractorActions.changeIsFromJoinCompletePage(false));
          }

          setIsContinueAtSellTractorModalOpen(true);

          if (data.id) {
            dispatch(
              userOwnTractorActions.changeOwnTractorIdForEdit({
                ownTractorIdForEdit: data.id,
              }),
            );
          }
        },
        onError: (error) => {
          if (error instanceof AxiosError) {
            if (!error.response) {
              setIsRerequestPopupModalOpen(true);
            } else {
              setIsSyetemErrorPopupModalOpen(true);
            }
          }
        },
      },
    );
  };

  useEffect(() => {
    const { marketPrice: savedMarketPrice } = userOwnTractorSurveySelector;
    setMarketPrice(savedMarketPrice);
  }, [userOwnTractorSurveySelector]);

  return (
    <>
      <Helmet>
        <title>{ownTractorPageInfo.marketPricePage.title}</title>
      </Helmet>

      <OwnTractorSurveyPageWrapper
        headerTitle={'중고 시장에서 이 트랙터의\n가격은 얼마인가요?'}
        isLoading={isPostUsersOwnTractorLoading}
        canConfirm
        confirmHandler={handleSurveySubmit}
        confirmButtonText={'완료'}
        steps={steps}
        currentStep={currentStep}
        prevButtonHandler={() => {
          navigate('/survey/progress/own-tractor/required-input-overview', { replace: true });
        }}
      >
        <SurveyForm>
          <SurveyInputSection>
            <SurveyInputHeader inputTitle={'중고 시장 적정가'} isOptional />
            <InputContainer>
              <BasicInputWithExtras
                type={'number'}
                inputValue={marketPrice}
                placeholder={'예) 6500'}
                onChange={(value: InputValue) => setMarketPrice(value as number)}
              >
                <Surfix>{'만원'}</Surfix>
              </BasicInputWithExtras>
              <PriceHelperText price={marketPrice!} />
            </InputContainer>
          </SurveyInputSection>
        </SurveyForm>
      </OwnTractorSurveyPageWrapper>

      <SlideConfirmModal
        isShow={isContinueAtSellTractorModalOpen}
        height={210}
        description={'수수료 없는 플루바 마켓에서\n내 트랙터 판매 등록 해보시겠어요?'}
        confirmButton={{ handleClick: confirmSlideModal, text: '네, 등록할게요!' }}
        closeButton={{ handleClick: closeSlideModal, text: '없어요. 완료할게요.' }}
      />

      <PopupConfirmModal
        isShow={isNeedJoinModalOpen}
        hasCloseButton={false}
        title={'회원가입이 필요해요!'}
        description={'입력하신 트랙터를 등록 하시려면\n회원 가입이 필요합니다.'}
        buttonArray={[
          {
            text: '카카오톡으로 간편 로그인하기',
            onClick: () => {
              navigate(pageInfo.loginPage.path, { replace: true });
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => {
              setIsNeedJoinModalOpen(false);
            },
            color: 'gray',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isEnrollCompletePopupModalOpen}
        hasCloseButton={false}
        title={'등록 완료! 🎉'}
        description={'참여 해주셔서 감사합니다.\n입력한 정보를 기반으로\n맞춤 서비스를 제공해 드릴게요!'}
        buttonArray={[
          {
            text: '이전 페이지로 돌아가기',
            onClick: confirmPopupModal,
            color: 'blue',
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isRerequestPopupModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsRerequestPopupModalOpen(false)}
        description={'요청 시간이 초과되었어요 🙂\n아래 버튼을 눌러 다시 요청해주세요!'}
        buttonArray={[
          {
            text: '등록 완료 하기',
            onClick: handleSurveySubmit,
            color: 'blue',
            isLoading: isPostUsersOwnTractorLoading,
            disabled: isPostUsersOwnTractorLoading,
          },
        ]}
      />
      <PopupConfirmModal
        isShow={isSyetemErrorPopupModalOpen}
        hasCloseButton={false}
        closeModal={() => setIsRerequestPopupModalOpen(false)}
        description={'시스템 에러로 등록이 불가능합니다👨🏻‍🔧\n잠시후에 다시 이용해주세요.'}
        buttonArray={[
          {
            text: '홈으로',
            onClick: () => navigate(pageInfo.homePage.path),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 24px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;

const Surfix = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */

  white-space: nowrap;
`;
